import { useQuery } from "@tanstack/react-query";

/**
 * Live market data hook.
 *
 * FX rates: Frankfurter (https://www.frankfurter.dev) — ECB reference rates, no key.
 *   - Spot rate: GET /latest?base=EUR&symbols=USD
 *   - Yesterday's rate (for % change): GET /YYYY-MM-DD?base=EUR&symbols=USD
 * Crypto: CoinGecko free API — no key, includes 24h change inline.
 *
 * Both feeds refresh every 60s and are cached in React Query so the hero ticker
 * and the sidebar heatmap share a single network round-trip.
 */

export type MarketInstrument = {
  symbol: string;       // e.g. "EUR/USD"
  label: string;        // pretty label
  price: number;        // current price/rate
  change: number;       // absolute change vs. previous close
  changePct: number;    // % change vs. previous close
  kind: "fx" | "crypto" | "metal";
};

const FX_PAIRS: { symbol: string; base: string; quote: string }[] = [
  { symbol: "EUR/USD", base: "EUR", quote: "USD" },
  { symbol: "GBP/USD", base: "GBP", quote: "USD" },
  { symbol: "USD/JPY", base: "USD", quote: "JPY" },
  { symbol: "AUD/USD", base: "AUD", quote: "USD" },
  { symbol: "USD/CAD", base: "USD", quote: "CAD" },
  { symbol: "USD/CHF", base: "USD", quote: "CHF" },
];

// CoinGecko ids → display symbols
const CRYPTO_IDS = [
  { id: "bitcoin", symbol: "BTC/USD", label: "Bitcoin" },
  { id: "ethereum", symbol: "ETH/USD", label: "Ethereum" },
  { id: "solana", symbol: "SOL/USD", label: "Solana" },
  { id: "ripple", symbol: "XRP/USD", label: "XRP" },
];

function yesterdayISO(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  // Frankfurter falls back to the last available business day if needed.
  return d.toISOString().slice(0, 10);
}

async function fetchFx(): Promise<MarketInstrument[]> {
  // Frankfurter only returns rates relative to a single base, so we issue one
  // request per (base, quote) pair. They're all small, no-auth GETs that the
  // CDN heavily caches — total payload < 5KB.
  const yday = yesterdayISO();

  const results: (MarketInstrument | null)[] = await Promise.all(
    FX_PAIRS.map(async ({ symbol, base, quote }): Promise<MarketInstrument | null> => {
      try {
        const [latestRes, prevRes] = await Promise.all([
          fetch(`https://api.frankfurter.dev/v1/latest?base=${base}&symbols=${quote}`),
          fetch(`https://api.frankfurter.dev/v1/${yday}?base=${base}&symbols=${quote}`),
        ]);
        if (!latestRes.ok || !prevRes.ok) throw new Error("frankfurter http error");
        const latest = await latestRes.json();
        const prev = await prevRes.json();
        const price = latest?.rates?.[quote];
        const prevPrice = prev?.rates?.[quote];
        if (typeof price !== "number" || typeof prevPrice !== "number") return null;
        const change = price - prevPrice;
        const changePct = (change / prevPrice) * 100;
        return { symbol, label: symbol, price, change, changePct, kind: "fx" };
      } catch {
        return null;
      }
    }),
  );

  return results.filter((r): r is MarketInstrument => r !== null);
}

async function fetchCrypto(): Promise<MarketInstrument[]> {
  const ids = CRYPTO_IDS.map((c) => c.id).join(",");
  try {
    const res = await fetch(
      `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true`,
    );
    if (!res.ok) throw new Error("coingecko http error");
    const data = await res.json();
    const mapped: (MarketInstrument | null)[] = CRYPTO_IDS.map(({ id, symbol, label }) => {
      const row = data?.[id];
      if (!row || typeof row.usd !== "number") return null;
      const price = row.usd as number;
      const changePct = (row.usd_24h_change as number) ?? 0;
      const change = (price * changePct) / 100;
      return { symbol, label, price, change, changePct, kind: "crypto" };
    });
    return mapped.filter((r): r is MarketInstrument => r !== null);
  } catch {
    return [];
  }
}

export function useMarketData() {
  const fx = useQuery({
    queryKey: ["market", "fx"],
    queryFn: fetchFx,
    refetchInterval: 60_000,
    staleTime: 45_000,
    refetchOnWindowFocus: false,
  });

  const crypto = useQuery({
    queryKey: ["market", "crypto"],
    queryFn: fetchCrypto,
    refetchInterval: 60_000,
    staleTime: 45_000,
    refetchOnWindowFocus: false,
  });

  return {
    fx: fx.data ?? [],
    crypto: crypto.data ?? [],
    all: [...(fx.data ?? []), ...(crypto.data ?? [])],
    isLoading: fx.isLoading || crypto.isLoading,
    isError: fx.isError && crypto.isError,
    refetch: () => {
      fx.refetch();
      crypto.refetch();
    },
  };
}

/** Format a price based on instrument kind so 1.0834 stays compact and 67342.10 reads clean. */
export function formatPrice(value: number, kind: MarketInstrument["kind"]): string {
  if (kind === "crypto") {
    if (value >= 1000) return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
    if (value >= 1) return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
    return value.toLocaleString("en-US", { maximumFractionDigits: 4 });
  }
  // FX
  if (value >= 50) return value.toFixed(2); // JPY pairs
  return value.toFixed(4);
}
